/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useVoicemailBroadcastGroupsList.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing the Voicemail Broadcast Groups list, integrating with useDataGrid and broadcastGroupsApi.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchBroadcastGroups } from '../api/broadcastGroupsApi';
import type { VoicemailBroadcastGroup } from '../types';

interface UseVoicemailBroadcastGroupsListOptions {
  /** Optional pre-configured columns. If empty, defaults are used */
  initialColumns?: { key: string; label: string; visible: boolean; sortable: boolean; render?: (value: unknown, item: VoicemailBroadcastGroup) => React.ReactNode }[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function useVoicemailBroadcastGroupsList({
  initialColumns = [],
  initialSortBy = 'code',
  initialSortOrder = 'asc',
  initialLimit = 10,
  domainId
}: UseVoicemailBroadcastGroupsListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchBroadcastGroups({
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          domainId
        });

        return {
          data: result.data,
          pagination: result.pagination
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : t('voicemailsBroadcastGroups:messages.loadError');
        showError(message);
        throw error;
      }
    },
    [showError, t, domainId]
  );

  const availableColumns = useMemo(() => {
    if (initialColumns.length > 0) {
      return initialColumns;
    }

    return [
      {
        key: 'code',
        label: t('voicemailsBroadcastGroups:columns.code'),
        visible: true,
        sortable: true
      },
      {
        key: 'name',
        label: t('voicemailsBroadcastGroups:columns.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'description',
        label: t('voicemailsBroadcastGroups:columns.description'),
        visible: true,
        sortable: true
      },
      {
        key: 'members',
        label: t('voicemailsBroadcastGroups:columns.members'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: VoicemailBroadcastGroup) => {
          const count = item?.members ? item.members.length : 0;
          return count ? <span className="text-sm">{count}</span> : '-';
        }
      },
      {
        key: 'enabled',
        label: t('voicemailsBroadcastGroups:columns.enabled'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: VoicemailBroadcastGroup) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
          >
            {item?.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t, initialColumns]);

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
    moduleName: 'voicemails-broadcast-groups'
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}


